import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isDefined, isNotDefined } from '@enterprise/core';

import type { WindowFeatureValue } from './types/window-feature-value.type';
import type { WindowFeature } from './types/window-feature.type';
import type { WindowHandle } from './types/window-handle.type';
import type { WindowOpenOptions } from './types/window-open.options';

/**
 * Stable default features object defined outside the hook to avoid re-creating
 * a new reference on every render, which would break `useCallback` memoization.
 *
 * Note: `noopener` severs the opener's handle to the child window, which means
 * `window.closed` will always read `false` and `isOpen` polling will not reflect
 * the true state. Remove `noopener` from features if accurate `isOpen` tracking
 * is required.
 */
const DEFAULT_FEATURES: Partial<Record<WindowFeature, WindowFeatureValue>> = {
  noopener: 1,
  noreferrer: 1,
};

/** Interval in milliseconds at which the opened window's `closed` property is polled. */
const POLL_INTERVAL_MS = 500;

/**
 * React hook to open a new browser window with customizable options and features.
 *
 * Behavior notes:
 * - If the previously opened window is still open, calling `open` again will
 *   navigate it to the new URL rather than spawning a second window. If no URL
 *   is passed, the last used URL is reused.
 * - `isOpen` is kept in sync via polling and will reflect the window being closed
 *   by the user within one poll tick. This does not work when `noopener` is set
 *   in features, as the browser severs the opener's handle to the child window.
 * - `isBlocked` is set when the browser's popup blocker prevents the window from
 *   opening and resets on the next successful call.
 * - When `closeOnUnmount` is `true`, the opened window is closed if the component
 *   using this hook unmounts while the window is still open.
 *
 * @param options - Optional configuration for the window, including features, focus behavior, and target.
 * @returns A tuple containing:
 *   - `open`: A callback to open or navigate the window to the given URL. If omitted,
 *     the last used URL is reused.
 *   - `handle`: A {@link WindowHandle} with reactive state and a `close` callback.
 *
 * @example
 * const [openWindow, { isOpen, isBlocked, close }] = useWindowOpen({ focus: true });
 *
 * openWindow('https://example.com');
 *
 * if (isBlocked) {
 *   return <p>Popup was blocked. Please allow popups for this site.</p>;
 * }
 */
export const useWindowOpen = (options?: WindowOpenOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const windowRef = useRef<Window | null>(null);
  const lastUrlRef = useRef<null | string>(null);

  const { closeOnUnmount = false, features = DEFAULT_FEATURES, focus = true, target = '_blank' } = options ?? {};

  if (process.env.NODE_ENV === 'development' && features.noopener) {
    console.warn(
      'useWindowOpen: `noopener` is set in features but polling relies on reading `window.closed`. ' +
        'The browser severs the opener handle when `noopener` is active, so `isOpen` will not ' +
        'reflect the true state of the window. Remove `noopener` from features if accurate ' +
        '`isOpen` tracking is required.'
    );
  }

  const resetState = useCallback(() => {
    windowRef.current = null;
    setIsOpen(false);
  }, []);

  const close = useCallback(() => {
    if (isDefined(windowRef.current) && !windowRef.current.closed) {
      windowRef.current.close();
    }

    resetState();
  }, [resetState]);

  const open = useCallback(
    (url?: string) => {
      const resolvedUrl = url ?? lastUrlRef.current;

      if (isNotDefined(resolvedUrl)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('useWindowOpen: `open` was called without a URL and no previous URL is stored.');
        }

        return;
      }

      lastUrlRef.current = resolvedUrl;

      // Reuse the existing window if it is still open, rather than spawning a new one.
      if (isDefined(windowRef.current) && !windowRef.current.closed) {
        windowRef.current.location.href = resolvedUrl;

        if (focus) {
          windowRef.current.focus();
        }

        return;
      }

      const featureParts: string[] = [];

      for (const key of Object.keys(features) as WindowFeature[]) {
        const value = features[key];

        if (isNotDefined(value)) {
          continue;
        }

        featureParts.push(`${key}=${value}`);
      }

      const wnd = window.open(resolvedUrl, target, featureParts.join(','));

      if (isDefined(wnd)) {
        if (focus) {
          wnd.focus();
        }

        windowRef.current = wnd;
        setIsOpen(true);
        setIsBlocked(false);
      } else {
        setIsBlocked(true);
      }
    },
    [features, focus, target]
  );

  // Poll window.closed since there is no close event observable from the opener.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const interval = setInterval(() => {
      if (windowRef.current?.closed === true) {
        resetState();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen, resetState]);

  // Optionally close the window if the consuming component unmounts.
  useEffect(() => {
    if (!closeOnUnmount) {
      return;
    }

    return () => {
      close();
    };
  }, [close, closeOnUnmount]);

  const handle = useMemo<WindowHandle>(() => {
    return { close, isBlocked, isOpen };
  }, [isOpen, isBlocked, close]);

  return [open, handle] as const;
};
